import { getPersianLabel } from '@/lib/persianUi';
import {
    addISODateDays,
    jalaliToISO,
    jalaaliMonthDays,
    toJalaliParts,
} from '@/lib/format';

/**
 * Filter vocabulary and date-preset helpers for the reports section.
 *
 * Presets are expressed on the Persian calendar because that is the calendar
 * the business reports on, but they resolve to plain Gregorian ISO dates — the
 * backend takes Gregorian `date_from` / `date_to` and only uses `calendar` to
 * choose the labels on the month buckets.
 */

export const GRANULARITIES = [
    { value: 'day', label: 'روزانه' },
    { value: 'week', label: 'هفتگی' },
    { value: 'month', label: 'ماهانه' },
];

export const CALENDARS = [
    { value: 'jalali', label: 'شمسی' },
];

/** Mirrors the backend `DeliveryStatus` enum; empty means "no filter". */
export const STATUSES = [
    { value: '', label: 'همه وضعیت‌ها' },
    { value: 'pending', label: 'در انتظار' },
    { value: 'processing', label: 'در حال پردازش' },
    { value: 'shipped', label: 'ارسال شده' },
    { value: 'delivered', label: 'تحویل شده' },
    { value: 'failed', label: 'ناموفق' },
];

export const STATUS_LABELS = Object.fromEntries(
    STATUSES.filter((s) => s.value).map((s) => [s.value, s.label])
);

/**
 * Mirrors the backend `TransactionStatus` enum. The API echoes the raw enum
 * back in the row `label`, so the Persian wording has to come from here.
 */
export const TRANSACTION_STATUS_LABELS = {
    pending: 'در انتظار',
    verified: 'تأیید شده',
    already_verified: 'قبلاً تأیید شده',
    failed: 'ناموفق',
    expired: 'منقضی شده',
};

/** Persian label for a transaction status, never exposing the raw enum. */
export const transactionStatusLabel = (status) =>
    getPersianLabel(TRANSACTION_STATUS_LABELS, status, 'reports.transactionStatus');

export const DEFAULT_FILTERS = {
    from: null,
    to: null,
    granularity: 'day',
    calendar: 'jalali',
    status: '',
};

const dateFromISO = (value) => new Date(`${value}T12:00:00Z`);

const addDays = (date, days) => dateFromISO(addISODateDays(date, days));

/** First day of the Persian month that contains `reference`. */
export const jalaliMonthStart = (reference = new Date()) => {
    const parts = toJalaliParts(reference);
    return parts ? dateFromISO(jalaliToISO(parts.jy, parts.jm, 1)) : new Date(Number.NaN);
};

/** Last day of the Persian month that contains `reference`. */
export const jalaliMonthEnd = (reference = new Date()) => {
    const parts = toJalaliParts(reference);
    return parts
        ? dateFromISO(jalaliToISO(parts.jy, parts.jm, jalaaliMonthDays(parts.jy, parts.jm)))
        : new Date(Number.NaN);
};

/** First day of the Persian year that contains `reference`. */
export const jalaliYearStart = (reference = new Date()) => {
    const parts = toJalaliParts(reference);
    return parts ? dateFromISO(jalaliToISO(parts.jy, 1, 1)) : new Date(Number.NaN);
};

/** Gregorian ISO date (`YYYY-MM-DD`) representing the Tehran calendar day. */
export const toIsoDate = (date) => {
    const parts = toJalaliParts(date);
    return parts ? jalaliToISO(parts.jy, parts.jm, parts.jd) : '';
};

const today = () => dateFromISO(toIsoDate(new Date()));
const daysAgo = (days) => toIsoDate(addDays(today(), -(days - 1)));

/**
 * Preset ranges. `from`/`to` are ISO strings or null (null = "let the backend
 * default to the last 30 days").
 */
export const PRESETS = [
    { key: '7d', label: '۷ روز', resolve: () => ({ from: daysAgo(7), to: toIsoDate(new Date()) }) },
    { key: '30d', label: '۳۰ روز', resolve: () => ({ from: daysAgo(30), to: toIsoDate(new Date()) }) },
    { key: '90d', label: '۹۰ روز', resolve: () => ({ from: daysAgo(90), to: toIsoDate(new Date()) }) },
    {
        key: 'this-month',
        label: 'این ماه',
        resolve: () => ({
            from: toIsoDate(jalaliMonthStart()),
            to: toIsoDate(jalaliMonthEnd()),
        }),
    },
    {
        key: 'last-month',
        label: 'ماه گذشته',
        resolve: () => {
            const previousMonthDay = addDays(jalaliMonthStart(), -1);
            return {
                from: toIsoDate(jalaliMonthStart(previousMonthDay)),
                to: toIsoDate(jalaliMonthEnd(previousMonthDay)),
            };
        },
    },
    {
        key: 'this-year',
        label: 'امسال',
        resolve: () => ({ from: toIsoDate(jalaliYearStart()), to: toIsoDate(new Date()) }),
    },
];

/** Which preset (if any) the current range matches, for highlighting the chip. */
export const matchPreset = (from, to) => {
    if (!from || !to) return null;
    const found = PRESETS.find((preset) => {
        const range = preset.resolve();
        return range.from === from && range.to === to;
    });
    return found?.key ?? null;
};
